import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import ModalitiesRatesChart from '../components/ModalitiesRatesChart.jsx'

const asArray = (value) => (Array.isArray(value) ? value : [])

/** Copy may be a plain string or an array of string segments and `{ bold: '…' }` chunks (paragraphs & bullets). */
const renderServicesInvestmentRichText = (p) => {
  if (typeof p === 'string') return p
  if (!Array.isArray(p)) return null
  return p.map((chunk, j) => {
    if (typeof chunk === 'string') {
      return <React.Fragment key={j}>{chunk}</React.Fragment>
    }
    if (chunk && typeof chunk === 'object' && chunk.bold != null) {
      return (
        <strong key={j} className="font-bold">
          {chunk.bold}
        </strong>
      )
    }
    return null
  })
}

const ServicesInvestment = () => {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const location = useLocation()

  const href = (path) => (language === 'pt' ? `/pt${path}` : path)
  const sections = asArray(t('servicesInvestment.sections'))

  usePageSeo({
    enabled: !isLoading,
    title: `${t('servicesInvestment.meta.title')} | VIVA Psychotherapy`,
    description: t('servicesInvestment.meta.description'),
    pathname: location.pathname
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
        <header className="text-center mb-8 sm:mb-10 max-w-3xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-darkGreen tracking-tight">
            {t('servicesInvestment.pageTitle')}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-dark-800/90 leading-relaxed">{t('servicesInvestment.intro')}</p>
        </header>

        <section className="mb-12 sm:mb-14 lg:mb-16" aria-labelledby="services-investment-rates-heading">
          <h2
            id="services-investment-rates-heading"
            className="text-lg sm:text-xl font-bold text-dark-800 text-center mb-6 sm:mb-8"
          >
            {t('servicesInvestment.ratesSectionTitle')}
          </h2>
          <ModalitiesRatesChart listAriaLabel={t('servicesInvestment.ratesSectionTitle')} />
        </section>

        <div className="max-w-3xl mx-auto space-y-10 sm:space-y-12">
          {sections.map((section, index) => (
            <section key={section.title || index} className="scroll-mt-20">
              <h2 className="text-lg sm:text-xl font-bold text-primary-darkGreen mb-3">{section.title}</h2>
              {asArray(section.paragraphs).map((p, i) => (
                <p key={i} className="text-gray-700 leading-relaxed mb-3 last:mb-0">
                  {renderServicesInvestmentRichText(p)}
                </p>
              ))}
              {section.bullets && section.bullets.length > 0 && (
                <ul className="mt-4 list-disc pl-5 space-y-2 text-gray-700 leading-relaxed">
                  {section.bullets.map((item, i) => (
                    <li key={i}>{renderServicesInvestmentRichText(item)}</li>
                  ))}
                </ul>
              )}
              {section.cta?.label && section.cta?.path && (
                <p className="mt-5">
                  <Link
                    to={href(section.cta.path)}
                    className="inline-flex items-center gap-1.5 font-semibold text-primary-darkGreen underline decoration-primary-darkGreen/35 underline-offset-2 transition-colors hover:text-primary-darkGreen/85 hover:decoration-primary-darkGreen/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-darkGreen/50 rounded-sm"
                  >
                    {section.cta.label}
                    <svg
                      className="h-4 w-4 shrink-0 opacity-80"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.15"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M2 6h6M7.25 3.75L10.25 6L7.25 8.25" />
                    </svg>
                  </Link>
                </p>
              )}
            </section>
          ))}
        </div>
      </article>
    </div>
  )
}

export default ServicesInvestment
